import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import FullscreenPanel from '../game/FullscreenPanel';
import { useAuthStore } from '../../store/auth-store';
import './PlayerProfileOverlay.css';

/**
 * `ProfilePage` must be reached through a DYNAMIC import, not a static one.
 *
 * `ProfilePage` imports `GameReplay`, and `GameReplay` imports this overlay to
 * make its player names clickable. A static import here would close the cycle
 * `GameReplay → PlayerProfileOverlay → ProfilePage → GameReplay`; `lazy()` keeps
 * it out of the static graph. It also costs nothing: App.tsx already loads
 * `ProfilePage` through the very same lazy chunk.
 */
const ProfilePage = lazy(() => import('./ProfilePage'));
/** La modale di segnalazione e il suo foglio scendono solo a chi preme. */
const PlayerReportModal = lazy(() => import('./PlayerReportModal'));

interface PlayerProfileOverlayProps {
  /** Real account username. Callers must filter out guests/bots first. */
  username: string;
  onClose: () => void;
}

/**
 * Another player's public profile, opened on top of whatever you were doing.
 *
 * Deliberately an overlay rather than a screen change: during a live game
 * App.tsx force-navigates back to the board whenever the multiplayer status is
 * `playing`, so a `setScreen('profile')` would be undone instantly. Opening the
 * profile over the game also keeps the board (and the clock) alive underneath,
 * and in the game review it preserves the ply you were studying.
 *
 * È l'UNICO modo in cui si apre il profilo di un altro giocatore: da qui, dalle
 * classifiche, dai collegati, dai tornei, dallo storico. Prima erano due — da
 * alcuni posti si cambiava schermata — e quella differenza aveva gia' lasciato un
 * bottone muto nella classifica dentro una partita, dove il cambio di schermata
 * non puo' funzionare.
 *
 * `FullscreenPanel` already provides the portal, the back-gesture stack entry,
 * Escape/backdrop/X closing and the background scroll lock.
 */
export function PlayerProfileOverlay({ username, onClose }: PlayerProfileOverlayProps) {
  const { t } = useTranslation();

  /*
   * Lo stato della segnalazione sta QUI e non dentro `ProfilePage`, perche' la
   * bandierina vive nella TESTATA del pannello — accanto al nome di chi si sta
   * guardando — e la testata e' roba di questo componente.
   */
  const [segnalaAperto, setSegnalaAperto] = useState(false);
  const [giaSegnalato, setGiaSegnalato] = useState(false);
  /*
   * Tre valori e non due: `null` vuol dire «non so ancora».
   *
   * Partendo da `true` la bandierina comparirebbe SUBITO, prima che la richiesta
   * del profilo risponda, per poi sparire su un 404: un lampo. Partendo da
   * `false` sarebbe giusta ma per caso. Con `null` il pulsante aspetta di sapere,
   * ed e' la stessa rete che copre il profilo inesistente — un ospite, un bot, un
   * account cancellato: li' la card non viene disegnata ma la testata resta.
   */
  const [profiloEsiste, setProfiloEsiste] = useState<boolean | null>(null);

  /*
   * Il ripristino al cambio di giocatore non arriva piu' da solo.
   *
   * `ProfilePage` si rimonta sul suo `key={username}`, ma questo componente no: i
   * posti che lo montano tengono UNA istanza e le cambiano il nome. Senza questo,
   * segnalato un giocatore, la bandierina resterebbe spenta anche sul successivo.
   */
  useEffect(() => {
    setSegnalaAperto(false);
    setGiaSegnalato(false);
    setProfiloEsiste(null);
  }, [username]);

  /* Chi guarda, se e' REGISTRATO: `viewerUsername` e' gia' null per gli ospiti e
     per gli anonimi, quindi una condizione sola copre due porte. Il confronto
     ignora le maiuscole perche' il lookup lato server le ignora. */
  const viewerUsername = useAuthStore((s) => (s.isGuest ? null : s.user?.username ?? null));
  const mostraSegnala =
    profiloEsiste === true &&
    viewerUsername !== null &&
    viewerUsername.toLowerCase() !== username.toLowerCase() &&
    !giaSegnalato;

  const esito = useCallback((esiste: boolean) => { setProfiloEsiste(esiste); }, []);

  return (
    <FullscreenPanel
      title={username}
      closeLabel={t('common.close')}
      azioneTitolo={
        mostraSegnala ? (
          <button
            type="button"
            className="fs-panel-azione ppo-segnala"
            onClick={() => setSegnalaAperto(true)}
            aria-label={t('profile.report.trigger', { username })}
          >
            <Flag size={18} aria-hidden="true" />
          </button>
        ) : undefined
      }
      onClose={onClose}
    >
      {/* `.fs-panel-body` is overflow:hidden and delegates scrolling to its child. */}
      <div className="ppo-scroll">
        <Suspense fallback={null}>
          <ProfilePage
            key={username}
            username={username}
            onBack={onClose}
            embedded
            onEsitoCaricamento={esito}
          />
        </Suspense>
      </div>
      {/*
        DENTRO il pannello e non accanto, ed e' una differenza che si vede solo da
        desktop. Il fondale della modale chiude al clic e non ferma la
        propagazione; i portali di React fanno risalire l'evento nell'albero dei
        COMPONENTI, non del DOM. Da fratello risalirebbe fino a chi ha montato
        questo pannello — e ConnectedUsersModal si disegna dentro un
        `<div className="modal-overlay" onClick={onClose}>`: un clic sul fondale
        della segnalazione avrebbe chiuso anche l'elenco dei collegati sotto. Qui
        dentro l'evento muore sullo `stopPropagation` di `.fs-panel`.
      */}
      {segnalaAperto && (
        <Suspense fallback={null}>
          <PlayerReportModal
            username={username}
            onClose={() => setSegnalaAperto(false)}
            onReported={() => setGiaSegnalato(true)}
          />
        </Suspense>
      )}
    </FullscreenPanel>
  );
}

export default PlayerProfileOverlay;
